/**
 * Gold (per_student) and Silver (fixed) subscription plans.
 */

import { computeNextPaymentDue } from "./paymentNextDue";
import { isLiberiaSchool } from "./currencyFormatter";

export const PLAN_GOLD = "per_student";
export const PLAN_SILVER = "fixed";

/**
 * Set to `true` when Silver should appear again on Pricing.
 * Kept false for now so schools only see Gold.
 */
export const SILVER_PLAN_PUBLIC = false;

const DEFAULT_FIXED_MONTHLY = 400;
const DEFAULT_FIXED_TERMLY = 1400;
const DEFAULT_FIXED_YEARLY = 4800;

/** Silver plan checkout discounts (not applied to monthly). */
export const SILVER_DISCOUNT_TERMLY = 0.05;
export const SILVER_DISCOUNT_YEARLY = 0.08;

export function isSilverPlanAvailable(school, admin) {
    if (!SILVER_PLAN_PUBLIC) return false;
    return !isLiberiaSchool(school, admin);
}

export function isSilverPlan(school, admin) {
    // Schools already on Silver keep Silver behavior even while the plan is hidden on Pricing
    if (!school) return false;
    if (isLiberiaSchool(school, admin)) return false;
    return String(school.subscriptionPackage || PLAN_GOLD).toLowerCase() === PLAN_SILVER;
}

export function isGoldPlan(school) {
    return !isSilverPlan(school);
}

export function getPlanId(school) {
    return isSilverPlan(school) ? PLAN_SILVER : PLAN_GOLD;
}

export function getPlanDisplayName(planId) {
    return String(planId).toLowerCase() === PLAN_SILVER ? "Silver" : "Gold";
}

export function getSchoolPlanDisplayName(school) {
    return getPlanDisplayName(getPlanId(school));
}

export function getBillingCycle(school) {
    const raw = school && school.billingCycle;
    const cycle = String(raw || "termly").toLowerCase();
    if (cycle === "monthly" || cycle === "yearly") return cycle;
    return "termly";
}

export function getBillingCycleLabel(cycle) {
    if (cycle === "monthly") return "Month";
    if (cycle === "yearly") return "Year";
    return "Term";
}

export function getFixedPlanBaseAmountGhs(school, billingCycle) {
    const cycle = billingCycle || getBillingCycle(school);
    if (cycle === "yearly") {
        const n = parseFloat(school && school.fixedPlanYearlyGhs);
        return Number.isFinite(n) && n > 0 ? n : DEFAULT_FIXED_YEARLY;
    }
    if (cycle === "monthly") {
        const n = parseFloat(school && school.fixedPlanMonthlyGhs);
        return Number.isFinite(n) && n > 0 ? n : DEFAULT_FIXED_MONTHLY;
    }
    const n = parseFloat(school && school.fixedPlanTermGhs);
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_FIXED_TERMLY;
}

export function getSilverBillingDiscountRate(billingCycle) {
    const cycle = billingCycle || "termly";
    if (cycle === "termly") return SILVER_DISCOUNT_TERMLY;
    if (cycle === "yearly") return SILVER_DISCOUNT_YEARLY;
    return 0;
}

export function getSilverBillingDiscountPercent(billingCycle) {
    return Math.round(getSilverBillingDiscountRate(billingCycle) * 100);
}

/** Checkout amount for Silver (base minus termly/yearly discount). */
export function getFixedPlanAmountGhs(school, billingCycle) {
    const cycle = billingCycle || getBillingCycle(school);
    const base = getFixedPlanBaseAmountGhs(school, cycle);
    const rate = getSilverBillingDiscountRate(cycle);
    if (!rate) return base;
    return Math.round(base * (1 - rate) * 100) / 100;
}

export function computeSubscriptionAmountPesewasForSchool(school, activeStudentCount, charge) {
    if (isSilverPlan(school)) {
        const ghs = getFixedPlanAmountGhs(school);
        return (ghs * 100).toFixed(2);
    }
    const amount_ = (parseFloat(activeStudentCount) * 100 * charge).toFixed(2);
    const period = school && school.period;
    if (period !== undefined && period !== "" && period !== "monthly") {
        return (parseFloat(amount_) * 3.5).toFixed(2);
    }
    return amount_;
}

export function computeNextPaymentDueForSchool(school, dateYmd, monthsNames) {
    if (isSilverPlan(school)) {
        const parts = String(dateYmd).trim().split("-");
        if (parts.length < 3) return null;
        const y = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10);
        const d = parseInt(parts[2], 10);
        if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return null;
        const cycle = getBillingCycle(school);
        const addMonths = cycle === "yearly" ? 12 : cycle === "monthly" ? 1 : 4;
        const next = new Date(y, m - 1, d);
        if (Number.isNaN(next.getTime())) return null;
        next.setMonth(next.getMonth() + addMonths);
        const pad2 = (n) => String(n).padStart(2, "0");
        const yN = next.getFullYear();
        const mN = next.getMonth() + 1;
        const dN = next.getDate();
        const months = monthsNames || [];
        return {
            next: `${yN}-${pad2(mN)}-${pad2(dN)}`,
            nextmonth: months[mN - 1] || "",
            nextyear: yN,
            nextday: String(dN),
        };
    }
    return computeNextPaymentDue(dateYmd, school && school.period, monthsNames);
}

export function getSubscriptionPeriodLabel(school) {
    if (isSilverPlan(school)) {
        return getBillingCycleLabel(getBillingCycle(school));
    }
    const period = school && school.period;
    if (period !== undefined && period !== "" && period !== "monthly") return "Term";
    return "Month";
}

export function formatSilverCyclePriceLabel(school, billingCycle) {
    const cycle = billingCycle || getBillingCycle(school);
    const checkout = getFixedPlanAmountGhs(school, cycle);
    const pct = getSilverBillingDiscountPercent(cycle);
    const label = getBillingCycleLabel(cycle);
    if (!pct) {
        return `${label} — GHS ${checkout.toLocaleString()}`;
    }
    return `${label} — GHS ${checkout.toLocaleString()} (${pct}% off)`;
}

export function getSilverCheckoutDiscountNote(school) {
    if (!isSilverPlan(school)) return "";
    const pct = getSilverBillingDiscountPercent(getBillingCycle(school));
    if (!pct) return "";
    const base = getFixedPlanBaseAmountGhs(school);
    const checkout = getFixedPlanAmountGhs(school);
    return `${pct}% Silver discount applied (was GHS ${base.toLocaleString()}) — you pay GHS ${checkout.toLocaleString()}`;
}
